import { useState} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../Components/Layout/Layout';
import ProductSearchList from '../Components/Products/ProductSearchList'; 
import { Input } from "@/Components/ui/input";
import { Button } from "@/Components/ui/button"; 
import { ChevronLeft, Plus, Search } from "lucide-react";
import axiosInstance from '../utils/axiosConfig';
import { foodbookInstance } from '../utils/axiosConfig';

const Productsearch = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [activeTab] = useState('all');
    const { returnPage } = location.state || {};
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);


    const handleSearch = async () => {
        if (!inputValue || inputValue.trim() === '') {
            setProducts([]);
            return;
        }

        try {
            setIsLoading(true); 
            const response = await foodbookInstance.post(`/MenuApp/SearchResults`, {
                keyword: inputValue,
                "filters": [],
                "pageIndex": 0,
                "pageSize": 200
            });
            const data = response.data;

            if (Array.isArray(data)) {
                setProducts(data);
            } else if (data.products && Array.isArray(data.products)) {
                setProducts(data.products);
            } else {
                console.error('Unexpected API response format:', data);
                setProducts([]);
            }
        } catch (error) {
            console.error('Error fetching products:', error);
            setError(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault(); // Voorkom de standaard form submit
            setSearchTerm(inputValue);
            handleSearch();
        }
    };

    const handleInputChange = (e) => {
        setInputValue(e.target.value);
    };

    const handleProductClick = async (product) => {
        try {
            setIsSaving(true);

            const foodbookResponse = await foodbookInstance.get(`/MenuApp/GetProductSheet/${product.id}`);

            if (foodbookResponse.status === 200) {
                // Initialiseer de allergenen lijst
                product.allergens = [];

                // Controleer of er allergeninfo is en loop er doorheen
                const allergenInfo = foodbookResponse?.data?.product?.specificationinfolist?.specificationinfo?.allergenset?.allergens?.allergeninfo || [];

                allergenInfo.forEach(allergeen => {
                    if (parseInt(allergeen.levelofcontainmentid) === 1) { //1 bevat en 3 is kan bevatten
                        product.allergens.push({
                            id: parseInt(allergeen.id),
                            name: allergeen.name.value // Let op: name is nu een object met een value property
                        });
                    }
                });

                product.farmtofarm = foodbookResponse?.data?.product?.farmtofarm ?? 0;
                product.refid = product.id;

                const response = await axiosInstance.post(`/product/AddProduct`, product);
                if (response.status !== 200) {
                    throw new Error('Failed to add product');
                } else {
                    handleReturn();
                }
            } else {
                throw new Error('Failed to fetch product details from Foodbook');
            }
        } catch (error) {
            console.error('Error add product:', error);
            setError(error.message);
        } finally {
            setIsSaving(false);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleAddProduct = () => {
        navigate('/product', {
            state: {
                productId: 0,
                returnPage: 'productsearch'
            }
        });
    };

    const handleReturn = () => {
        navigate(`/products`, { state: { activeTab } });
    };

    if (isLoading) return <Layout><div>Loading...</div></Layout>;
    if (error) return <Layout><div>Error: {error}</div></Layout>;

    return (
        <Layout>
            <div className="relative max-w-[1100px] mx-auto">
                <div className="mb-8">
                    <button
                        onClick={handleReturn}
                        className="text-ps-blue-dark hover:underline font-inter"
                    >
                        <ChevronLeft className="float-left" />
                        Terug naar overzicht
                    </button>
                </div>

                <div className="relative">
                    <h2 className="text-xl font-normal font-inter text-ps-blue-dark mb-8">Zoek product</h2>
                    <form onSubmit={(e) => e.preventDefault()}>
                        <div className="relative mb-4">
                            <Input
                                type="text"
                                value={inputValue}
                                onChange={handleInputChange}
                                onKeyPress={handleKeyPress}
                                disabled={isSaving}
                                placeholder="Zoek product..."
                                className="w-full p-2 pl-10 border rounded bg-white"
                            />
                            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                            <Button
                                onClick={handleSearch}
                                className="inline-flex absolute right-0 top-0 items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-blue-dark hover:bg-ps-blue-dark/90 text-white px-8 py-2 rounded"
                            >
                                <span>Zoeken</span>
                            </Button>
                        </div>
                    </form>
                    <br />

                    <div className="max-h-[45vh] overflow-y-auto">
                        <ProductSearchList products={products} onProductClick={handleProductClick} disabled={isSaving} />
                    </div>
                    <div className="flex justify-end gap-4 mt-8">
                        <Button
                            onClick={handleAddProduct}
                            className="inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 bg-ps-green hover:bg-ps-green/90 text-white px-4 py-2 shadow-lg rounded-full"
                        >
                            <Plus className="h-6 w-6" />
                            <span>Handmatig toevoegen</span>
                        </Button>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Productsearch;